<template>
  <div class="article-card">
    <img
      :alt="article?.title"
      :src="getImageFromProxy(article?.img || '', '400:300')"
      class="preview"
      loading="lazy"
    >
    <p
      :style="{ color: article?.category?.color }"
      class="category mb12 f14"
    >
      {{ article?.category.title }}
    </p>

    <template v-if="isLangsEqual || isPostShowed">
      <nuxt-link
        :aria-label="article?.title"
        :rel="!isLangsEqual ? 'nofollow noindex' : ''"
        :to="localePath(`/blog/${article?.category?.link}/${article?.link}`)"
        class="article-card-link"
      />
      <p
        class="title-h3 mb16 highlight-words"
        v-html="computedTitle"
      />
    </template>

    <div class="f">
      <div
        v-tooltip="article?.author?.persona_name"
        class="avatar-wrapper"
      >
        <img
          :src="getImageFromProxy(article?.author?.avatar_url || '', '30:30')"
          alt="moderator avatar"
          class="avatar"
        >
      </div>
      <span class="f14">{{ $t('read_time', { time: article?.read_time }) }} <client-only>| {{
        moment(article?.published_at).format('DD MMMM, YYYY')
      }}</client-only></span>
    </div>

    <LazyPagesBlogGradientPreview
      v-if="!isLangsEqual && !isPostShowed"
      :link="localePath(`/blog/${article?.category?.link}/${article?.link}`)"
      :title="article?.title || ''"
      rel="nofollow noindex"
      @show-card="isPostShowed = true"
    />
  </div>
</template>

<script lang="ts" setup>
import type { BlogPost } from '~/graphql/generated/schema-types';
import moment from 'moment';
import { languagesEnum } from '~/types/LocalesToCockies';

const props = defineProps<{
  article?: BlogPost
}>();
const { locale } = useI18n();
const localePath = useLocalePath();
moment.locale(locale.value);
const isPostShowed = ref(false);

const computedTitle = computed(() => {
  return (props.article?.title?.length || 0) > 70 ? `${props.article?.title?.slice(0, 70)}...` : props.article?.title;
});

const isLangsEqual = computed(() => {
  return languagesEnum[locale.value.toUpperCase() as keyof typeof languagesEnum] === props.article?.lang || isPostShowed.value;
});
</script>

<style lang="scss" scoped>
.article-card {
  padding: 12px;
  border-radius: 12px;
  background: var(--surfaces-low-container);
  position: relative;
  transition: all 0.3s;

  &:hover {
    transform: scale(1.05);
  }

  .highlight-words:deep(b) {
    color: var(--accent-secondary);
  }

  .article-card-link {
    position: absolute;
    inset: 0;
  }

  .preview {
    width: 100%;
    height: 170px;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 16px;
    object-position: bottom left;
  }

  .avatar {
    width: 24px;
    height: 24px;
    object-fit: cover;
    border-radius: 50%;

    &-wrapper {
      position: relative;
      z-index: 1;
    }
  }
}
</style>
